'use client';

import { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import Image from 'next/image';
import { useInView } from 'react-intersection-observer';
import { ClassSession, ClassName } from '@/types/class';
import { getClassCardImageByClassName } from '@/utils/imageSettings';
import styles from './UpcomingSessions.module.css';
import { trackEvent } from '@/lib/analytics/firebaseAnalytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { Play, Pause } from 'lucide-react';

interface UpcomingClass extends ClassSession {
  id: string;
  imageUrl: string;
}

const UpcomingSessions = () => {
  const [upcomingClasses, setUpcomingClasses] = useState<UpcomingClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    loadUpcomingSessions();
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile && isPlaying && upcomingClasses.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % upcomingClasses.length);
      }, 4000); // Change slide every 4 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isMobile, isPlaying, upcomingClasses.length]);

  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const loadUpcomingSessions = async () => {
    try {
      const now = new Date();

      const classesRef = collection(db, 'classes');
      const q = query(
        classesRef,
        where('startTime', '>', Timestamp.fromDate(now)),
        orderBy('startTime', 'asc'),
        limit(24)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setLoading(false);
        return;
      }

      const activeDocs = snapshot.docs.filter((d) => (d.data() as ClassSession).isCancelled !== true);

      // Load images for each class
      const classesWithImages = await Promise.all(
        activeDocs.map(async (doc) => {
          const classData = doc.data() as ClassSession;
          const imageUrl = await getClassCardImageByClassName(classData.className);

          return {
            ...classData,
            id: doc.id,
            imageUrl,
          };
        })
      );

      setUpcomingClasses(classesWithImages.slice(0, 3));

      // Track impression
      trackEvent(AnalyticsEvents.VIEW_UPCOMING_SESSIONS, {
        sessions_count: classesWithImages.slice(0, 3).length,
      });
    } catch (error) {
      console.error('[UpcomingSessions] Error loading upcoming sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp: Timestamp): string => {
    const date = timestamp.toDate();

    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `${dayOfWeek}, ${month} ${day} • ${time} Local time`;
  };

  const handleClassClick = (classId: string, className: string) => {
    trackEvent(AnalyticsEvents.CLICK_UPCOMING_SESSION, {
      class_id: classId,
      class_name: className,
    });

    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPlaying(!isPlaying);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  // Hide section if no upcoming classes
  if (!loading && upcomingClasses.length === 0) {
    return null;
  }

  return (
    <section id="upcoming" className={styles.upcomingSessions} ref={ref}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <h2 className={inView ? styles.fadeIn : ''}>Upcoming Classes</h2>
          <p className={`${styles.scheduleInfo} ${inView ? styles.fadeIn : ''}`}>
            Classes are on Zoom every Sunday and Tuesday at 19:30 (UTC+3). Instruction is in English.
          </p>
        </div>

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
          </div>
        ) : (
          <>
            {/* Desktop: Grid Layout */}
            <div className={upcomingClasses.length === 1 ? styles.desktopGridCentered : styles.desktopGrid}>
              {upcomingClasses.map((classSession, index) => (
                <div
                  key={classSession.id}
                  className={`${styles.sessionCard} ${inView ? styles.fadeInUp : ''}`}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  onClick={() => handleClassClick(classSession.id, classSession.className)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClassClick(classSession.id, classSession.className);
                    }
                  }}
                >
                  <div className={styles.imageWrapper}>
                    <Image
                      src={classSession.imageUrl}
                      alt={classSession.className}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      style={{ objectFit: 'cover' }}
                      className={styles.image}
                    />
                    <div className={styles.imageOverlay}></div>
                  </div>

                  <div className={styles.cardContent}>
                    <div className={styles.classInfo}>
                      <h3 className={styles.className}>{classSession.className}</h3>
                      <p className={styles.instructor}>{classSession.instructor}</p>
                    </div>

                    <div className={styles.sessionDetails}>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>When</span>
                        <span className={styles.detailValue}>
                          {formatDateTime(classSession.startTime)}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Format</span>
                        <span className={styles.detailValue}>Online</span>
                      </div>
                    </div>

                    <div className={styles.cardFooter}>
                      <span className={styles.joinText}>Click to join →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: Carousel */}
            <div className={styles.mobileCarousel}>
              <div className={styles.carouselWrapper}>
                <div
                  className={styles.carouselTrack}
                  style={{
                    transform: `translateX(-${currentIndex * 100}%)`,
                  }}
                >
                  {upcomingClasses.map((classSession) => (
                    <div
                      key={classSession.id}
                      className={styles.carouselSlide}
                      onClick={() => handleClassClick(classSession.id, classSession.className)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={styles.slideImageWrapper}>
                        <Image
                          src={classSession.imageUrl}
                          alt={classSession.className}
                          fill
                          sizes="100vw"
                          style={{ objectFit: 'cover' }}
                          className={styles.slideImage}
                        />
                        <div className={styles.slideOverlay}></div>
                      </div>

                      <div className={styles.slideContent}>
                        <h3 className={styles.slideClassName}>{classSession.className}</h3>
                        <p className={styles.slideInstructor}>{classSession.instructor}</p>

                        <div className={styles.slideDetails}>
                          <div className={styles.slideDetailRow}>
                            <span className={styles.slideLabel}>When</span>
                            <span className={styles.slideValue}>
                              {formatDateTime(classSession.startTime)}
                            </span>
                          </div>
                          <div className={styles.slideDetailRow}>
                            <span className={styles.slideLabel}>Format</span>
                            <span className={styles.slideValue}>Online</span>
                          </div>
                        </div>

                        <div className={styles.slideFooter}>
                          <span className={styles.slideJoinText}>Tap to join →</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Carousel Controls */}
              {upcomingClasses.length > 1 && (
                <div className={styles.carouselControls}>
                  <button
                    className={styles.playPauseButton}
                    onClick={togglePlayPause}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                  </button>

                  <div className={styles.dotsContainer}>
                    {upcomingClasses.map((_, index) => (
                      <button
                        key={index}
                        className={`${styles.dot} ${index === currentIndex ? styles.dotActive : ''}`}
                        onClick={() => goToSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default UpcomingSessions;
